import { cookies } from "@utils/cookies";
import {
  mapUTMParamsToParamObject,
  isAnyParamsFound,
} from "@utils/UTMParamUtils";
import { handleUniversalTooltip } from "@utils/universalTooltip";
import { isPerformanceCookieConsentGranted } from "@utils/consentCookieUtils";
import initializeDeepLinker from "@internal-libs/deep-linking/conductor";
import { breakpointConfig } from "@utils/breakpointConfigUtils";
import { createWaypoint, WaypointInstance } from "@utils/intersectionObserverWrapper";

/** Custom event names consumed by PageController — import these in other modules. */
export const PAGE_EVENTS = {
  SCROLL_TO: "pageController:scrollTo",
  SCROLL_TO_BOTTOM: "pageController:scrollToBottom",
} as const;

export function dispatchScrollTo(anchor: HTMLElement): void {
  document.dispatchEvent(
    new CustomEvent(PAGE_EVENTS.SCROLL_TO, { detail: { anchor } }),
  );
}

export function dispatchScrollToBottom(anchor: HTMLElement): void {
  document.dispatchEvent(
    new CustomEvent(PAGE_EVENTS.SCROLL_TO_BOTTOM, { detail: { anchor } }),
  );
}

interface BreakpointQuery {
  name: string;
  string: string;
}

const BREAKPOINTS: BreakpointQuery[] = [
  { name: "xsmall", string: `(max-width: ${breakpointConfig.bpem_xsmall_max})` },
  {
    name: "small",
    string: `(min-width: ${breakpointConfig.bpem_small_min}) and (max-width: ${breakpointConfig.bpem_small_max})`,
  },
  {
    name: "medium",
    string: `(min-width: ${breakpointConfig.bpem_medium_min}) and (max-width: ${breakpointConfig.bpem_medium_max})`,
  },
  {
    name: "large",
    string: `(min-width: ${breakpointConfig.bpem_large_min}) and (max-width: ${breakpointConfig.bpem_large_max})`,
  },
  { name: "xlarge", string: `(min-width: ${breakpointConfig.bpem_xlarge_min})` },
];

export class PageController {
  private el: HTMLElement;
  private mqls: { mql: MediaQueryList; handler: (e: MediaQueryListEvent) => void }[] = [];
  private currentMqClass: string | null = null;
  private loginInterval: ReturnType<typeof setInterval> | null = null;
  private boundConsentHandler = this.extractUTMParameters.bind(this);
  private boundScrollTo = this.handleScrollTo.bind(this);
  private boundScrollToBottom = this.handleScrollToBottom.bind(this);
  private boundVisibilityCheck = this.checkTokenOnVisibility.bind(this);
  private cookieStoreController: AbortController | null = null;
  private waypointInstance: WaypointInstance | null = null;

  constructor(el: HTMLElement) {
    this.el = el;
  }

  static initAll(root: Document | HTMLElement = document): void {
    const elements = root.querySelectorAll<HTMLElement>('[data-component="page-controller"]');
    elements.forEach((el) => {
      const pc = new PageController(el);
      pc.init();
    });
  }

  init(): void {
    this.registerQueries();

    document.addEventListener(PAGE_EVENTS.SCROLL_TO, this.boundScrollTo as EventListener);
    document.addEventListener(PAGE_EVENTS.SCROLL_TO_BOTTOM, this.boundScrollToBottom as EventListener);

    this.refreshAccessToken();
    this.checkLoginExpired();
    this.extractUTMParameters();
    window.addEventListener("consent.onetrust", this.boundConsentHandler);

    initializeDeepLinker();
    handleUniversalTooltip();

    // Hide meta-nav on scroll down — global page-level behaviour (moved from Section)
    this.waypointInstance = createWaypoint({
      element: document.body,
      handler: (direction: string) => {
        document.body.classList.toggle("hide-meta-nav", direction === "down");
      },
      offset: -25,
    });
  }

  destroy(): void {
    document.removeEventListener(PAGE_EVENTS.SCROLL_TO, this.boundScrollTo as EventListener);
    document.removeEventListener(PAGE_EVENTS.SCROLL_TO_BOTTOM, this.boundScrollToBottom as EventListener);
    window.removeEventListener("consent.onetrust", this.boundConsentHandler);

    if (this.loginInterval !== null) {
      clearInterval(this.loginInterval);
      this.loginInterval = null;
    }

    document.removeEventListener("visibilitychange", this.boundVisibilityCheck);

    if (this.cookieStoreController) {
      this.cookieStoreController.abort();
      this.cookieStoreController = null;
    }

    this.mqls.forEach(({ mql, handler }) => mql.removeEventListener("change", handler));
    this.mqls = [];
    this.waypointInstance?.destroy();
  }

  hasAccessToken(): boolean {
    return document.cookie.split("; ").some((c) => c.startsWith("SERVICES_ACCESS_TOKEN="));
  }

  private registerQueries(): void {
    BREAKPOINTS.forEach((query) => {
      const mql = window.matchMedia(query.string);
      const handler = (e: MediaQueryListEvent) => {
        if (e.matches) {
          this.viewportMatch(query, "match");
        }
      };

      mql.addEventListener("change", handler);
      this.mqls.push({ mql, handler });

      if (mql.matches) {
        this.viewportMatch(query, "match");
      }
    });
  }

  private viewportMatch(query: BreakpointQuery, state: string): void {
    const html = document.documentElement;
    const nextClass = `mq-${query.name}`;

    // Swap only the changed class instead of removing all five then re-adding one
    // (avoids an extra style recalc on every breakpoint change).
    if (this.currentMqClass !== nextClass) {
      if (this.currentMqClass) {
        html.classList.remove(this.currentMqClass);
      }
      html.classList.add(nextClass);
      this.currentMqClass = nextClass;
    }

    document.dispatchEvent(
      new CustomEvent("viewportChange", { detail: { query, state } }),
    );
  }

  private checkLoginExpired(): void {
    if (!document.body.classList.contains("js-cug")) {
      return;
    }

    // Prefer CookieStore API (event-driven, no polling)
    if ("cookieStore" in window) {
      this.cookieStoreController = new AbortController();
      (window as any).cookieStore.addEventListener(
        "change",
        (event: any) => {
          const deleted = event.deleted.some(
            (c: any) => c.name === "SERVICES_ACCESS_TOKEN",
          );
          if (deleted) {
            location.reload();
          }
        },
        { signal: this.cookieStoreController.signal },
      );
      return;
    }

    // Fallback: check on tab visibility change + infrequent poll
    document.addEventListener("visibilitychange", this.boundVisibilityCheck);
    this.loginInterval = setInterval(() => {
      if (!this.hasAccessToken()) {
        location.reload();
      }
    }, 300_000); // 5 min fallback for always-visible tabs
  }

  private checkTokenOnVisibility(): void {
    if (document.visibilityState === "visible" && !this.hasAccessToken()) {
      location.reload();
    }
  }

  private refreshAccessToken(): void {
    if (this.hasAccessToken()) {
      fetch("/bin/cug/refresh.json?tokenRenewal=true", { credentials: "same-origin" });
    }
  }

  private extractUTMParameters(): void {
    if (!isPerformanceCookieConsentGranted()) {
      return;
    }

    const filteredParams = mapUTMParamsToParamObject();

    if (isAnyParamsFound(filteredParams)) {
      cookies.set("UTM_PARAMS", JSON.stringify(filteredParams), {
        days: 1,
        domain: ".straumann.com",
      });
    }
  }

  private handleScrollTo(e: CustomEvent<{ anchor: HTMLElement }>): void {
    e.detail.anchor.scrollIntoView({ behavior: "smooth" });
  }

  private handleScrollToBottom(e: CustomEvent<{ anchor: HTMLElement }>): void {
    const target = e.detail.anchor;
    const navBar = document.querySelector<HTMLElement>(".comp-nav-bar");
    const offset = navBar?.offsetHeight ?? 0;
    window.scrollTo({
      top: target.offsetTop + target.offsetHeight - offset,
      behavior: "smooth",
    });
  }
}
